/** Passive test receipts must distinguish overlapping restores of the same agent. */
export type ClaimBootstrapProxy = Readonly<{
  id: string;
  roomId: string;
  embedded: boolean;
}>;

export type ClaimBootstrapPhase =
  | 'proxy-created'
  | 'initial-token'
  | 'proxy-start'
  | 'state-sync-answered'
  | 'state-sync-backstop'
  | 'state-sync-error'
  | 'reset-received'
  | 'reset-ignored'
  | 'reset-token'
  | 'bootstrap-enter'
  | 'bootstrap-exit'
  | 'restore-disabled'
  | 'snapshot-load'
  | 'template-load'
  | 'analytics-model-load'
  | 'shadow-kind-read'
  | 'shadow-send-called'
  | 'proxy-destroyed';

type Identity = Readonly<{
  proxyInstance: number;
  agentId: string;
  roomId: string;
}>;

export type ClaimBootstrapObservationRun = Readonly<{
  bootstrapInstance: number;
  origin: 'initial' | 'reset';
}>;

type ClaimAgentStatus =
  | 'absent'
  | 'unknown'
  | 'booting'
  | 'idle'
  | 'offline-embedded'
  | 'offline'
  | 'online';

/** Read only this Studio's existing agent status, never its registry record. */
export const readClaimBootstrapAgentStatus = (
  agent: Readonly<{ roomId: string; status: unknown }> | undefined,
  roomId: string
): ClaimAgentStatus => {
  if (agent === undefined || agent.roomId.toLowerCase() !== roomId.toLowerCase()) return 'absent';
  const status = agent.status;
  return status === 'booting' ||
    status === 'idle' ||
    status === 'offline-embedded' ||
    status === 'offline' ||
    status === 'online'
    ? status
    : 'unknown';
};

type Event = Identity &
  Readonly<{
    ordinal: number;
    at: number;
    bootstrapInstance: number | null;
    origin: 'initial' | 'reset' | 'none';
    phase: ClaimBootstrapPhase;
    result: 'entered' | 'returned' | 'threw' | 'cancelled';
  }>;

type CurrentProxy = Readonly<{
  proxy: ClaimBootstrapProxy;
  started: boolean;
  stateHasGeneratedCamera: boolean;
}>;

/** Per rule: shared-infra-failure-is-not-same-call-site-evidence, global. */
export class ClaimBootstrapObservation {
  private enabled = false;
  private enabledAt: number | null = null;
  private readonly identities = new WeakMap<ClaimBootstrapProxy, Identity>();
  private readonly runs = new WeakMap<ClaimBootstrapObservationRun, Identity>();
  private readonly events: Event[] = [];
  private nextProxy = 0;
  private nextRun = 0;
  private nextOrdinal = 0;
  private overflow = false;
  private observationErrors = 0;

  /** Only the existing preload-gated probe enables this observer. */
  enable(): void {
    if (this.enabled) return;
    this.enabledAt = Date.now();
    this.enabled = true;
  }

  private identity(proxy: ClaimBootstrapProxy): Identity {
    const existing = this.identities.get(proxy);
    if (existing !== undefined) return existing;
    const identity = {
      proxyInstance: ++this.nextProxy,
      agentId: proxy.id,
      roomId: proxy.roomId
    };
    this.identities.set(proxy, identity);
    return identity;
  }

  open(
    proxy: ClaimBootstrapProxy,
    origin: 'initial' | 'reset'
  ): ClaimBootstrapObservationRun | undefined {
    if (this.enabled === false) return undefined;
    try {
      const identity = this.identity(proxy);
      const run = { bootstrapInstance: ++this.nextRun, origin };
      this.runs.set(run, identity);
      return run;
    } catch {
      this.observationErrors++;
      return undefined;
    }
  }

  record(
    proxy: ClaimBootstrapProxy,
    run: ClaimBootstrapObservationRun | undefined,
    phase: ClaimBootstrapPhase,
    result: Event['result']
  ): void {
    if (this.enabled === false) return;
    try {
      // Agent strings alone cannot let an old or foreign invocation adopt a proxy.
      const identity = this.identity(proxy);
      if (run !== undefined && this.runs.get(run) !== identity) {
        this.observationErrors++;
        return;
      }
      this.events.push({
        ...identity,
        ordinal: ++this.nextOrdinal,
        at: Date.now(),
        bootstrapInstance: run?.bootstrapInstance ?? null,
        origin: run?.origin ?? 'none',
        phase,
        result
      });
      if (this.events.length > 512) {
        this.events.shift();
        this.overflow = true;
      }
    } catch {
      // Diagnostics must not replace the original product result or exception.
      this.observationErrors++;
    }
  }

  read(
    current: readonly CurrentProxy[],
    agentId: string,
    roomId: string,
    agentStatus: ClaimAgentStatus = 'unknown'
  ) {
    const matches = (identity: Identity): boolean =>
      identity.agentId.toLowerCase() === agentId.toLowerCase() &&
      identity.roomId.toLowerCase() === roomId.toLowerCase();
    return {
      observedAt: Date.now(),
      agentStatus,
      // A lazily installed probe cannot explain operations before activation.
      enabled: this.enabled,
      enabledAt: this.enabledAt,
      firstRetainedOrdinal: this.events[0]?.ordinal ?? null,
      lastOrdinal: this.nextOrdinal,
      overflow: this.overflow,
      observationErrors: this.observationErrors,
      proxies: current.flatMap(({ proxy, started, stateHasGeneratedCamera }) => {
        if (
          proxy.id.toLowerCase() !== agentId.toLowerCase() ||
          proxy.roomId.toLowerCase() !== roomId.toLowerCase()
        )
          return [];
        const identity = this.identities.get(proxy);
        return [
          {
            proxyInstance: identity?.proxyInstance ?? null,
            present: true,
            agentId: proxy.id,
            roomId: proxy.roomId,
            embedded: proxy.embedded,
            started,
            stateHasGeneratedCamera
          }
        ];
      }),
      events: this.events.filter(matches).map((event) => ({ ...event }))
    };
  }
}

const observation = new ClaimBootstrapObservation();

export const enableClaimBootstrapObservation = (): void => observation.enable();
export const openClaimBootstrapObservation = (
  proxy: ClaimBootstrapProxy,
  origin: 'initial' | 'reset'
): ClaimBootstrapObservationRun | undefined => observation.open(proxy, origin);
export const recordClaimBootstrapPhase = (
  proxy: ClaimBootstrapProxy,
  run: ClaimBootstrapObservationRun | undefined,
  phase: ClaimBootstrapPhase,
  result: Event['result']
): void => observation.record(proxy, run, phase, result);
export const readClaimBootstrapObservation = (
  current: readonly CurrentProxy[],
  agentId: string,
  roomId: string,
  agentStatus: ClaimAgentStatus = 'unknown'
) => observation.read(current, agentId, roomId, agentStatus);
